import React, { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ChevronRight as ChevronIcon, Download as DownloadIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { tokens } from '../../../theme/tokens';
import { Pill } from './primitives';
import type { StagedUpdateSummary } from './productDetailTypes';
import { draftTouches } from './productDraft';
import { formatDateTime, money } from './productDetailFormat';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  remediating: 'Remediating',
  verifying: 'Verifying',
  review: 'Needs review',
  approved: 'Approved',
  failed: 'Failed',
};

const show = (value: unknown): string => {
  if (value == null || value === '') return '—';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value.length > 140 ? `${value.slice(0, 140)}…` : value;
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
  const text = JSON.stringify(value);
  return text.length > 140 ? `${text.slice(0, 140)}…` : text;
};

const isLockNote = (note?: string) => !!note && /^(Saved manually|Accepted AI)/.test(note);

const isPricePath = (path: string) => /^pricing\.(msrp|map|retail|salePrice)$/.test(path);

interface Props {
  staged: StagedUpdateSummary;
  editing: boolean;
  /** Paths the admin has changed in the current draft. */
  editedPaths: string[];
  currency: string;
}

export const StagedUpdateBanner: React.FC<Props> = ({ staged, editing, editedPaths, currency }) => {
  const [open, setOpen] = useState(false);
  const status = STATUS_LABELS[staged.stagingStatus] || staged.stagingStatus;
  // Real changes, plus fields the promotion keeps because an admin locked them.
  // Other "kept" rows (the staged row simply lacks the field) are noise here.
  const rows = staged.fields.filter((f) => f.kind === 'added' || f.kind === 'changed' || (f.kind === 'kept' && isLockNote(f.note)));
  const priceText =
    staged.priceDelta != null && staged.priceDelta !== 0
      ? ` · price ${staged.priceDelta > 0 ? '+' : '−'}${money(Math.abs(staged.priceDelta), currency).replace(/\.00$/, '')}`
      : '';
  const line = `${staged.changedCount} field${staged.changedCount === 1 ? '' : 's'} changed${priceText}${staged.updatedAt ? ` · scraped ${formatDateTime(staged.updatedAt)}` : ''}${staged.source ? ` from ${staged.source}` : ''}. Promoting it overwrites the live product.`;

  return (
    <Box sx={{ flex: '0 0 auto', mx: '22px', mt: '14px', border: `1px solid ${tokens.accent.border}`, borderRadius: `${tokens.radius.control}px`, bgcolor: tokens.signal.info.row, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: '14px', pr: 1.5, py: 1.25, flexWrap: 'wrap' }}>
        <DownloadIcon sx={{ fontSize: 17, color: tokens.accent.main, flex: '0 0 auto' }} />
        <Box sx={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <Typography component="span" sx={{ fontSize: 13.5, fontWeight: 600 }}>Staged update waiting</Typography>
            <Pill tone="caution">{status}</Pill>
            {staged.changeType && <Typography component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 11.5, color: tokens.ink.bodyAlt }}>{staged.changeType}</Typography>}
          </Stack>
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.bodyAlt }}>{line}</Typography>
        </Box>
        <Stack direction="row" useFlexGap sx={{ flex: '0 1 auto', flexWrap: 'wrap', gap: 1 }}>
          {rows.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
              endIcon={<ChevronIcon sx={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />}
              sx={{ height: 30, borderRadius: `${tokens.radius.small}px`, fontSize: 12.5, fontWeight: 600, bgcolor: tokens.surface.card, borderColor: tokens.accent.border, color: tokens.accent.hover }}
            >
              {open ? 'Hide changes' : 'Show changes'}
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            component={RouterLink}
            to={`/app/admin/staged-products?id=${encodeURIComponent(staged.id)}`}
            sx={{ height: 30, borderRadius: `${tokens.radius.small}px`, fontSize: 12.5, fontWeight: 600 }}
          >
            Review in Staged products
          </Button>
        </Stack>
      </Box>
      {open && (
        <Box sx={{ borderTop: `1px solid ${tokens.accent.tintStrong}`, bgcolor: tokens.surface.card }}>
          <Box sx={{ overflowX: 'auto', maxHeight: 240, overflowY: 'auto' }}>
            <Box sx={{ minWidth: 560 }}>
              <Box sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: tokens.surface.card, display: 'grid', gridTemplateColumns: { xs: 'minmax(120px, 180px) minmax(0, 1fr) minmax(0, 1fr) 140px', md: 'minmax(120px, 180px) minmax(0, 1fr) minmax(0, 1fr) 140px' }, columnGap: 1.75, px: '14px', py: '7px', borderBottom: `1px solid ${tokens.line.hairline}`, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.muted }}>
                <span>Field</span>
                <span>Live now</span>
                <span>Staged</span>
                <span />
              </Box>
              {rows.map((r) => {
                const editingThis = editing && draftTouches(editedPaths, r.path);
                const priceNote =
                  isPricePath(r.path) && typeof r.before === 'number' && typeof r.after === 'number'
                    ? `${r.after - r.before >= 0 ? '+' : '−'}${money(Math.abs(r.after - r.before), currency)}`
                    : '';
                const note = editingThis ? "You're editing this" : r.kind === 'kept' ? 'Kept (manual)' : priceNote || r.note || (r.kind === 'added' ? 'New' : '');
                return (
                  <Box key={r.path} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(120px, 180px) minmax(0, 1fr) minmax(0, 1fr) 140px', md: 'minmax(120px, 180px) minmax(0, 1fr) minmax(0, 1fr) 140px' }, columnGap: 1.75, alignItems: 'center', minHeight: 36, px: '14px', py: 0.75, borderBottom: `1px solid ${tokens.line.hairline}`, fontSize: 13 }}>
                    <Typography component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 12, color: tokens.ink.body, overflowWrap: 'anywhere' }}>{r.path}</Typography>
                    <Typography component="span" sx={{ fontSize: 13, color: tokens.ink.faint, textDecoration: r.kind === 'kept' ? 'none' : 'line-through', overflowWrap: 'anywhere' }}>{show(r.before)}</Typography>
                    <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: r.kind === 'kept' ? tokens.ink.faint : tokens.ink.primary, textDecoration: r.kind === 'kept' ? 'line-through' : 'none', overflowWrap: 'anywhere' }}>{show(r.after)}</Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 11.5,
                        textAlign: 'right',
                        color: editingThis ? tokens.signal.caution.ink : priceNote ? tokens.signal.positive.ink : tokens.ink.muted,
                      }}
                    >
                      {note}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '14px', py: '9px', fontSize: 12, color: tokens.ink.muted, flexWrap: 'wrap' }}>
            <Typography component="span" sx={{ flex: '1 1 260px', fontSize: 12, color: tokens.ink.muted }}>
              {editing
                ? 'Fields you save now are marked manual. The staged update skips them when it’s promoted.'
                : 'Fields marked manual are skipped when this update is promoted.'}
            </Typography>
            <Typography component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 11.5, color: tokens.ink.faint }}>stagedProducts/{staged.id}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
